import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, Select, DatePicker, Spin, Modal, message } from "antd";
import { connect } from "dva";
import moment from "moment";
import { router } from "umi";
import { useStates } from "@/utils/address";
import { Integrations, ShipmentStatus, TrailerType } from "@/utils/enum";
import { disableDatesBeforeToday, getUrlParams, checkUniqueAddresses, DEFAULT_ID } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import { fetchAssociatedUsers } from "@/services/entityGroups";
import { getSingleAdminShipment, getSingleShipment } from "@/services/shipments";
import { editAdminShipment, editShipment } from "@/services/shipments_new";
import { getAdminManagers, getAdminUserById } from "@/services/users_admin";
import useScreenDetector from "@/hooks/useScreenDetector";
import useAddresses from "@/components/AddressBookManager/useAddresses";
import DealerInput from "@/components/DealerInput";
import NotFoundContent from "@/components/NotFoundContent";
import { flatPhoneNumber } from "@/components/PhoneNumberInput";
import IntegrationInputs from "@/forms/shared/CreateShipment/components/IntegrationInputs";
import LocationInformationInputs from "@/forms/shared/CreateShipment/components/LocationInformationInputs";
import SpecialConditionsInputs from "@/forms/shared/CreateShipment/components/SpecialConditionsInputs";
import { parseAddressData } from "@/forms/shared/CreateShipment/utils";

const { Option } = Select;
const { confirm } = Modal;

const breakPoints = { xs: 24, s: 24, md: 24, lg: 12, xl: 12, xxl: 12 };

const EditShipmentForm = ({ configPerm }) => {
  const { shipmentId, admin } = getUrlParams(["shipmentId", "admin"]);
  const isAdmin = Boolean(admin);
  const { isMobile } = useScreenDetector();
  const { states: allStates, loading: loadingStates } = useStates();

  const editShipmentFormRef = useRef();
  const wwlIntegrationFormRef = useRef();
  const lastShipmentFetched = useRef(null);

  const searchManagerTimeoutRef = useRef(null);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managerSearch, setManagerSearch] = useState("");
  const [managers, setManagers] = useState([]);

  const [submitting, setSubmitting] = useState(false);
  const [isSpecialConditionsOpen, setIsSpecialConditionsOpen] = useState(false);
  const [wwlIntegrationInputsVisible, setWWLIntegrationInputsVisible] = useState(false);
  const [dealerInputHidden, setDealerInputHidden] = useState(true);
  const [loadingShipmentInfo, setLoadingShipmentInfo] = useState(true);
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [originalShipmentData, setOriginalShipmentData] = useState(null);
  const [dealersHookDisabled, setDealersHookDisabled] = useState(true);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const { addresses } = useAddresses({
    dealerId: selectedDealerId,
    configPerm,
  });
  const [isFormEdited, setIsFormEdited] = useState(false);
  const originalSelectedAddresses = useRef({}); // store a copy of the original addresses from the address book to check later if changes were made

  const handleIsSpecialConditionsOpenChange = useCallback(([isOpen]) => {
    setIsSpecialConditionsOpen(isOpen);
  }, []);

  const updateShipperSelectOptions = useCallback(
    async dealerId => {
      if (!configPerm["CoreService.AssociateUsers"]) {
        return;
      }

      setLoadingUsers(true);

      try {
        const res = await fetchAssociatedUsers(dealerId);
        const items = res?.items || [];
        setConnectedUsers(items);

        if (isAdmin && items.length === 1) {
          editShipmentFormRef.current.setFieldsValue({
            userId: items[0].userId,
          });
        }
      } catch (err) {
        console.error(err);
      }

      setLoadingUsers(false);
    },
    [configPerm, isAdmin],
  );

  const onSelectDealer = useCallback(
    dealer => {
      const { id: dealerId, integrations } = dealer;
      editShipmentFormRef?.current?.setFieldsValue({ userId: null });
      updateShipperSelectOptions(dealerId);

      if (Array.isArray(integrations)) {
        setWWLIntegrationInputsVisible(integrations.some(integration => integration.type === Integrations.WWL));
      }
      setSelectedDealerId(dealerId);
    },
    [updateShipperSelectOptions],
  );

  const handleSubmit = useCallback(
    async values => {
      // validate addresses are unique
      const validAddresses = checkUniqueAddresses(values.Pickup, values.Dropoff);

      if (!validAddresses) {
        message.error("Pickup and Dropoff addresses cannot be the same");
        return;
      }

      values.Pickup.contactPhone = flatPhoneNumber(values.Pickup.contactPhone);
      values.Dropoff.contactPhone = flatPhoneNumber(values.Dropoff.contactPhone);

      if (typeof values.Pickup.dayOfWeekRestricted === "string") {
        values.Pickup.dayOfWeekRestricted = values.Pickup.dayOfWeekRestricted.split(",");
      }

      if (typeof values.Dropoff.dayOfWeekRestricted === "string") {
        values.Dropoff.dayOfWeekRestricted = values.Dropoff.dayOfWeekRestricted.split(",");
      }

      values.Pickup.dayOfWeekRestricted = values.Pickup.dayOfWeekRestricted
        ? values.Pickup.dayOfWeekRestricted.sort().toString()
        : "";

      values.Dropoff.dayOfWeekRestricted = values.Dropoff.dayOfWeekRestricted
        ? values.Dropoff.dayOfWeekRestricted.sort().toString()
        : "";

      /**
       * @type {EditShipmentRequestData}
       */
      const editShipmentRequestData = {};

      editShipmentRequestData.isAvailable = values.isAvailable;

      if (!values.isAvailable) {
        editShipmentRequestData.availableAt = values.availableAt.toISOString();
      }

      editShipmentRequestData.trailerType = values.trailerType;
      editShipmentRequestData.autosledManagerId = values.autosledManagerId;

      const isNewPickupAddress = !addresses.find(address => address.name === values.Pickup.locationName);

      const isSamePickupAsOriginal = !checkUniqueAddresses(values.Pickup, originalSelectedAddresses.current.Pickup);

      if (values.Pickup.originalAddressId && isSamePickupAsOriginal) {
        editShipmentRequestData.originalPickupAddressId = isNewPickupAddress ? null : values.Pickup.originalAddressId;
      }

      // Pickup
      editShipmentRequestData.pickupAddress = parseAddressData(values.Pickup);
      editShipmentRequestData.pickupAddress.id = originalShipmentData.pickupAddress.id;
      editShipmentRequestData.pickupAddress.dealerId = values.dealerId;
      editShipmentRequestData.pickupAddress.notes = values.Pickup.instructionsOrRestrictions;

      // If auto filled from address book, include that addressId in request
      // If dropoff address name is not on address book, send null for the originalAddressId

      const isNewDropoffAddress = !addresses.find(address => address.name === values.Dropoff.locationName);

      // check if the pickup address is the same as the original one
      const isSameDropoffAsOriginal = !checkUniqueAddresses(values.Dropoff, originalSelectedAddresses.current.Dropoff);

      if (values.Dropoff.originalAddressId && isSameDropoffAsOriginal) {
        editShipmentRequestData.originalDropOffAddressId = isNewDropoffAddress
          ? null
          : values.Dropoff.originalAddressId;
      }

      // Dropoff
      editShipmentRequestData.dropOffAddress = parseAddressData(values.Dropoff);
      editShipmentRequestData.dropOffAddress.id = originalShipmentData.dropOffAddress.id;
      editShipmentRequestData.dropOffAddress.dealerId = values.dealerId;
      editShipmentRequestData.dropOffAddress.notes = values.Dropoff.instructionsOrRestrictions;

      editShipmentRequestData.dealerId = values.dealerId;

      if (values.userId) {
        editShipmentRequestData.contactShipperId = values.userId;
      }

      // Special conditions
      editShipmentRequestData.requiresTWIC = values.requiresTWIC ?? false;
      editShipmentRequestData.requiresEModal = values.requiresEModal ?? false;
      editShipmentRequestData.requiresLotExit = values.requiresLotExit ?? false;

      // Optional integration inputs
      if (wwlIntegrationInputsVisible) {
        const { vpc, railHead, carrierCode, sequenceNumber } = wwlIntegrationFormRef?.current?.getFieldsValue() ?? {};

        editShipmentRequestData.vpc = vpc;
        editShipmentRequestData.railHead = railHead;
        editShipmentRequestData.carrierCode = carrierCode;
        editShipmentRequestData.sequenceNumber = sequenceNumber;
        editShipmentRequestData.tenderFileUrl = "test";
        editShipmentRequestData.integrationType = Integrations.WWL;
      }

      setSubmitting(true);

      try {
        const endpoint = isAdmin ? editAdminShipment : editShipment;
        const response = await endpoint(shipmentId, editShipmentRequestData);

        if (response.error) {
          message.error("Something went wrong while editing the shipment.");
          throw new Error();
        }

        message.success("Successfully edited shipment!");

        const { id: shipmentIdResponse } = response;

        if (isAdmin) {
          router.push(`/admin/shipments/view?shipmentId=${shipmentIdResponse}`);
        } else {
          router.push(`/shipments/dealer/all/view?shipmentId=${shipmentIdResponse}`);
        }
      } catch (err) {
        message.error("Error creating shipment");
        console.error(err);
      } finally {
        setSubmitting(false);
      }
    },
    [wwlIntegrationInputsVisible, isAdmin, shipmentId, originalShipmentData, addresses],
  );

  const onSubmit = useCallback(
    async values => {
      if (isFormEdited) {
        confirm({
          title: "Warning",
          content: "Editing the address of a shipment will result in a re-quote. Are you sure you want to continue?",
          onOk() {
            handleSubmit(values);
          },
        });
        return;
      }

      handleSubmit(values);
    },
    [handleSubmit, isFormEdited],
  );

  const onSubmitError = useCallback(() => {
    message.error("Form is missing required fields");
  }, []);

  const onDealersFetched = useCallback(
    dealers => {
      // We need to wait until /core-service/dealers/allowed endpoint is called
      // by the DealerInput component because the response of that endpoint has
      // the integration info for each dealer, while the get shipment by id
      // endpoints dealer data hasn't
      if (!selectedDealerId) {
        // So if there isn't a previously selected dealer let's skip
        return;
      }

      const dealer = dealers.find(d => d.id === selectedDealerId);

      if (!dealer) {
        return;
      }

      setWWLIntegrationInputsVisible(dealer.integrations.some(integration => integration.type === Integrations.WWL));
    },
    [selectedDealerId],
  );

  useEffect(() => {
    if (allStates.length === 0 || lastShipmentFetched.current === shipmentId) {
      return;
    }

    lastShipmentFetched.current = shipmentId;
    setLoadingShipmentInfo(true);

    const endpoint = isAdmin ? getSingleAdminShipment : getSingleShipment;

    endpoint(shipmentId)
      .then(async res => {
        setOriginalShipmentData(res);
        setDealersHookDisabled(false);

        if (isAdmin) {
          setSelectedDealerId(res.dealer.id);
          onSelectDealer(res.dealer);
        }

        const pickupState = allStates.find(state => state.id === res.pickupAddress.stateId);

        const dropoffState = allStates.find(state => state.id === res.dropOffAddress.stateId);

        setIsSpecialConditionsOpen(res.requiresTWIC || res.requiresEModal || res.requiresLotExit);

        editShipmentFormRef.current.setFieldsValue({
          ...res,
          userId: res?.contactShipperId,
          autosledManagerId: res?.autosledManagerId,
          availableAt: res.availableAt ? moment(res.availableAt) : moment(),
          originalPickUpAddressId: res.pickupAddress.id,
          originalDropOffAddressId: res.dropOffAddress.id,
          Pickup: {
            ...res.pickupAddress,
            addressId: res.pickupAddress.id,
            stateName: pickupState?.name,
            stateShortName: pickupState?.shortName,
            locationName: res.pickupAddress.name,
            instructionsOrRestrictions: res.pickupAddress.notes,
            originalAddressId: res.originalPickupAddressId,
          },
          Dropoff: {
            ...res.dropOffAddress,
            addressId: res.dropOffAddress.id,
            stateName: dropoffState?.name,
            stateShortName: dropoffState?.shortName,
            locationName: res.dropOffAddress.name,
            instructionsOrRestrictions: res.dropOffAddress.notes,
            originalAddressId: res.originalDropOffAddressId,
          },
        });
      })
      .finally(() => {
        setLoadingShipmentInfo(false);
      });
  }, [isAdmin, shipmentId, allStates, loadingStates, onSelectDealer]);

  useEffect(() => {
    if (wwlIntegrationInputsVisible && wwlIntegrationFormRef.current) {
      wwlIntegrationFormRef.current.setFieldsValue({
        vpc: originalShipmentData.vpc,
        railHead: originalShipmentData.railHead,
        carrierCode: originalShipmentData.carrierCode,
        sequenceNumber: originalShipmentData.sequenceNumber,
      });
    }
  }, [
    wwlIntegrationInputsVisible,
    originalShipmentData?.vpc,
    originalShipmentData?.railHead,
    originalShipmentData?.carrierCode,
    originalShipmentData?.sequenceNumber,
  ]);

  const copyOriginalAddress = useCallback((address, type) => (originalSelectedAddresses.current[type] = address), []);

  const handleSearchAutosledManager = useCallback(text => {
    if (searchManagerTimeoutRef.current !== null) {
      clearTimeout(searchManagerTimeoutRef.current);
    }

    searchManagerTimeoutRef.current = setTimeout(() => {
      setManagerSearch(text);
      searchManagerTimeoutRef.current = null;
    }, 500);
  }, []);

  useEffect(() => {
    if (isAdmin) {
      const currentManagerId = originalShipmentData?.autosledManagerId || null;

      setLoadingManagers(true);

      getAdminManagers({
        page: 1,
        pageSize: 10,
        filter: managerSearch,
      })
        .then(async response => {
          const items = response || [];

          if (currentManagerId !== null && currentManagerId !== DEFAULT_ID) {
            const alreadyFetched = items.some(adm => adm.id === currentManagerId);

            if (!alreadyFetched) {
              const currentManager = await getAdminUserById(currentManagerId);
              items.push(currentManager);
            }
          }

          setManagers(items);
        })
        .finally(() => {
          setLoadingManagers(false);
        });
    }
  }, [managerSearch, isAdmin, originalShipmentData?.autosledManagerId]);

  return (
    <Spin spinning={loadingShipmentInfo}>
      <Form
        ref={editShipmentFormRef}
        layout="vertical"
        initialValues={{
          isAvailable: true,
          trailerType: TrailerType.Open,
          Pickup: {
            originalPickupAddressId: originalShipmentData?.originalPickupAddressId,
          },
          DropOff: {
            originalDropOffAddressId: originalShipmentData?.originalDropOffAddressId,
          },
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
        onFinish={onSubmit}
        onFinishFailed={onSubmitError}
        onValuesChange={changedValues => {
          const addressFields = ["addressLine1", "addressLine2", "city", "stateId", "zipCode"];

          const isPickupEdited = addressFields.some(field => Boolean(changedValues?.Pickup?.[field]));

          const isDropoffEdited = addressFields.some(field => Boolean(changedValues?.Dropoff?.[field]));

          if (isPickupEdited || isDropoffEdited) {
            setIsFormEdited(true);
          }
        }}
      >
        <Row gutter={16} style={{ width: "100%" }}>
          <Col flex={1}>
            <Row gutter={16} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <DealerInput
                formRef={editShipmentFormRef}
                xs={24}
                xl={8}
                disabled={originalShipmentData?.status >= ShipmentStatus.Delivered}
                hookDisabled={dealersHookDisabled}
                rules={[Validator.required("Please select a dealer")]}
                dealerId={selectedDealerId}
                onChange={onSelectDealer}
                onHiddenChange={setDealerInputHidden}
                onDealersFetched={onDealersFetched}
              />

              <Col xs={24} xl={dealerInputHidden ? 12 : 8}>
                <Form.Item
                  name="trailerType"
                  label="Trailer Type"
                  rules={[Validator.required("Please select a trailer type")]}
                  style={{ width: "100%" }}
                >
                  <Select
                    disabled={originalShipmentData?.status >= ShipmentStatus.Completed}
                    getPopupContainer={trigger => trigger.parentElement}
                  >
                    <Option value={TrailerType.Open}>Open</Option>
                    <Option value={TrailerType.Enclosed}>Enclosed</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} xl={dealerInputHidden ? 12 : 8}>
                <Form.Item name="availableAt" label="Available At" rules={[Validator.required("Please select a date")]}>
                  <DatePicker
                    format="MM/DD/YYYY"
                    disabled={originalShipmentData?.status >= ShipmentStatus.Delivered}
                    getPopupContainer={trigger => trigger.parentElement}
                    style={{ width: "100%" }}
                    disabledDate={disableDatesBeforeToday}
                  />
                </Form.Item>
              </Col>
            </Row>
            {isAdmin && (
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="userId" label="Shipper" rules={[Validator.required("Please select a user")]}>
                    <Select
                      showSearch
                      autoComplete="dontshow"
                      placeholder="Select a user"
                      disabled={originalShipmentData?.status >= ShipmentStatus.Delivered}
                      notFoundContent={NotFoundContent({
                        loading: loadingUsers,
                      })}
                      filterOption={(input, option) => option.key.toUpperCase().indexOf(input.toUpperCase()) !== -1}
                    >
                      {connectedUsers.map(user => (
                        <Select.Option
                          disabled={false}
                          key={`${user.userId || ""}${user.userName}`}
                          value={user.userId}
                        >
                          {user.userName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="autosledManagerId" label="Autosled Manager">
                    <Select
                      showSearch
                      autoComplete="dontshow"
                      defaultValue={originalShipmentData?.autosledManagerId}
                      filterOption={(input, option) => option.key.toUpperCase().indexOf(input.toUpperCase()) !== -1}
                      getPopupContainer={trigger => trigger.parentElement}
                      notFoundContent={NotFoundContent({
                        loading: loadingManagers,
                      })}
                      onSearch={handleSearchAutosledManager}
                    >
                      {(managers || []).map(adm => (
                        <Option value={adm.id} key={`${adm.id}${adm.fullName || ""}${adm.email}`}>
                          {adm.fullName} ({adm.email})
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col
                xs={breakPoints.xs}
                s={breakPoints.s}
                md={breakPoints.md}
                lg={breakPoints.lg}
                xl={breakPoints.xl}
                xxl={breakPoints.xxl}
                style={{ marginBottom: "20px" }}
              >
                <LocationInformationInputs
                  locationType="Pickup"
                  loading={loadingShipmentInfo}
                  formRef={editShipmentFormRef}
                  shipmentStatus={originalShipmentData?.status}
                  dealerId={selectedDealerId}
                  isAdmin={isAdmin}
                  onSelectAddress={address => copyOriginalAddress(address, "Pickup")}
                />
              </Col>
              <Col
                xs={breakPoints.xs}
                s={breakPoints.s}
                md={breakPoints.md}
                lg={breakPoints.lg}
                xl={breakPoints.xl}
                xxl={breakPoints.xxl}
                style={{ marginBottom: "20px" }}
              >
                <LocationInformationInputs
                  locationType="Dropoff"
                  loading={loadingShipmentInfo}
                  formRef={editShipmentFormRef}
                  shipmentStatus={originalShipmentData?.status}
                  dealerId={selectedDealerId}
                  isAdmin={isAdmin}
                  onSelectAddress={address => copyOriginalAddress(address, "Dropoff")}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {isAdmin && wwlIntegrationInputsVisible && (
          <div style={{ width: "100%" }}>
            <IntegrationInputs edit wwlIntegrationFormRef={wwlIntegrationFormRef} />
          </div>
        )}

        {isAdmin && (
          <div style={{ width: "100%" }}>
            <SpecialConditionsInputs
              disabled={originalShipmentData?.status >= ShipmentStatus.Completed}
              isOpen={isSpecialConditionsOpen}
              onIsOpenChange={handleIsSpecialConditionsOpenChange}
            />
          </div>
        )}

        <div
          style={{
            display: originalShipmentData?.status >= ShipmentStatus.Delivered ? "none" : "flex",
            width: "100%",
            justifyContent: isMobile ? "center" : "flex-end",
            marginTop: 16,
          }}
        >
          <Form.Item style={isMobile ? { width: "90%", marginBottom: 0 } : {}}>
            <Button type="primary" htmlType="submit" block={isMobile} loading={submitting}>
              Save
            </Button>
          </Form.Item>
        </div>
      </Form>
    </Spin>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(EditShipmentForm);
